import './StoreLocatorPage.scss';

import { b2x } from '@b2x/react/src';
import classnames from 'classnames';
import _ from 'lodash';
// import { useCounter } from '../useCounter';
import React from 'react';

import { Accordion } from '../Accordion';
import { Brand, ShopBrand, useAppContext } from '../AppContext';
import { ButtonVariant } from '../Button';
import { MiscellaneousContentType } from '../contentTypes';
import { ResourceKeys, t } from '../i18n/i18n';
import { IconName, IconSize } from '../Icon';
import currentPositionImage from '../images/currentPosition.png';
import pinBetter from '../images/pin/better.png';
import pinGolbet from '../images/pin/goldbet.png';
import pinIntralot from '../images/pin/intralot.png';
import pinLottomaticaServizi from '../images/pin/lottomaticaservizi.png';
import pinPlanetwin365 from '../images/pin/planetwin365.png';
import searchedPositionImage from '../images/searchedPosition.svg';
import logoBetter from '../images/thumbnail/better.svg';
import logoGolbet from '../images/thumbnail/goldbet.svg';
import logoIntralot from '../images/thumbnail/intralot.svg';
import logoLottomaticaServizi from '../images/thumbnail/lottomaticaservizi.svg';
import logoPlanetwin365 from '../images/thumbnail/planetwin365.svg';
import { Page } from './Page';

export interface StoreLocatorPageProps {}

export const StoreLocatorPage = (props: StoreLocatorPageProps) => {
  const miscellaneousContent = b2x.useContent<MiscellaneousContentType>('MISCELLANEOUS_CONTENT');

  const { brand, getBrandVariant } = useAppContext();
  const { routeInfo } = b2x.useAppContext();

  const title = getBrandVariant({
    goldbet: miscellaneousContent?.body.goldbetTitle,
    lottomatica: miscellaneousContent?.body.lottomaticaTitle,
    lottomaticaservizi: miscellaneousContent?.body.lottomaticaserviziTitle,
    planetwin365: miscellaneousContent?.body.planetwin365Title,
  });

  const getFixedFilters = React.useMemo<b2x.StoreLocatorProps<ButtonVariant, IconName, IconSize>['getFixedFilters']>(
    () => (filters) =>
      filters?.children
        .find((root) => root.code === 'FACE_BRAND')
        ?.children.filter((filter) => filter.tags?.includes(brand))
        .map((filter) => filter.id),
    [brand]
  );

  const shouldShowFilter = React.useCallback(
    (filter: b2x.ShopSearchFilter) => !!filter.filter.tags?.includes(brand),
    [brand]
  );

  return (
    <Page>
      <b2x.Helmet>
        <title>{title}</title>
        <meta
          content={getBrandVariant({
            goldbet: miscellaneousContent?.body.goldbetDescription,
            lottomatica: miscellaneousContent?.body.lottomaticaDescription,
            lottomaticaservizi: miscellaneousContent?.body.lottomaticaserviziDescription,
            planetwin365: miscellaneousContent?.body.planetwin365Description,
          })}
          name="description"
        />
      </b2x.Helmet>
      {/* Con un negozio aperto (segmento dinamico valorizzato) il WebPage lo emette la scheda negozio: deve essercene uno solo. */}
      {!routeInfo?.pathParam && <b2x.structuredData.WebPage brand={getSiteJsonLdBrand(brand)} name={title} />}
      <b2x.StoreLocator<ButtonVariant, IconName, IconSize>
        DetailsPanelServices={DetailsPanelServices}
        clustererAboveAvarageColor={getBrandVariant({
          goldbet: '#0A1B4E',
          lottomatica: '#003594',
          lottomaticaservizi: '#003594',
          planetwin365: '#fab615',
        })}
        clustererBelowAvarageColor={getBrandVariant({
          goldbet: '#0A1B4E',
          lottomatica: '#003594',
          lottomaticaservizi: '#003594',
          planetwin365: '#fab615',
        })}
        currentPositionImage={currentPositionImage}
        debug
        filtersRootDirectoryCode="FILTERS"
        filtersToggleButtonVariant="outline-gray-500"
        getDetailsPanelMarkerContent={getDetailsPanelMarkerContent}
        getFixedFilters={getFixedFilters}
        getMarkerContent={getMarkerContent}
        getShopDescription={getShopDescription}
        getShopName={getShopName}
        getShopOtherServices={getShopOtherServices}
        getShopThumbnailSrc={getShopThumbnailSrc}
        googlePlacesCountryRestrictions={['it']}
        hidePhoneButton
        iconArrowLeft="arrow-left"
        iconArrowRight="arrow-right"
        iconClose="closed"
        iconDirections="navigator"
        iconFilter="filters"
        iconMyLocation="geolocator"
        iconPhoneCall="call"
        iconPin="locator"
        iconSearch="search"
        iconShare="share"
        iconSpeechRecognition="voice-recorder"
        iconSpeechRecognitionActive="voice-recorder-def"
        iconZoomIn="plus"
        iconZoomOut="minus"
        iconsSize={24}
        searchedPositionImage={searchedPositionImage}
        shouldShowFilter={shouldShowFilter}
        showOpeningTime
        variantPrimary="brand-primary"
        variantSecondary="brand-secondary"
      />
    </Page>
  );
};

const DetailsPanelServices = ({ className, shop }: b2x.StoreLocatorDetailsPanelServicesProps) => {
  const bettingServices = getShopBettingServices(shop);
  const onlineGamesServices = getShopOnlineGamesServices(shop);
  const otherServices = getShopOtherServices(shop);
  const ppServices = getShopPPServices(shop);

  if (!bettingServices?.length && !onlineGamesServices?.length && !otherServices?.length && !ppServices?.length) {
    return null;
  }

  return (
    <div className={classnames('services', className)}>
      <h5 className="fw-bold mb-3">{t('shopDetails.services.title')}</h5>
      <Accordion id="services" variant="bordless">
        {bettingServices && bettingServices.length > 0 && (
          <b2x.AccordionItem id="betting" show title={t('shopDetails.services.types.betting')}>
            <ul className="list-unstyled m-0">
              {bettingServices.map((service) => (
                <li key={service.name}>{t(`shopDetails.services.${service.name}` as ResourceKeys)}</li>
              ))}
            </ul>
          </b2x.AccordionItem>
        )}
        {onlineGamesServices && onlineGamesServices.length > 0 && (
          <b2x.AccordionItem id="online-games" title={t('shopDetails.services.types.onlineGames')}>
            <ul className="list-unstyled m-0">
              {onlineGamesServices.map((service) => (
                <li key={service.name}>{t(`shopDetails.services.${service.name}` as ResourceKeys)}</li>
              ))}
            </ul>
          </b2x.AccordionItem>
        )}
        {otherServices && otherServices.length > 0 && (
          <b2x.AccordionItem id="other" title={t('shopDetails.services.types.otherServices')}>
            <ul className="list-unstyled m-0">
              {otherServices.map((service) => (
                <li key={service.name}>{t(`shopDetails.services.${service.name}` as ResourceKeys)}</li>
              ))}
            </ul>
          </b2x.AccordionItem>
        )}
        {ppServices &&
          ppServices.length > 0 &&
          ppServices.map((service) => (
            <b2x.AccordionItem
              className="pp-custom-accordion-disabled"
              disabled
              id={service.name}
              key={service.name}
              show={false}
              title={t(`shopDetails.services.${service.name}` as ResourceKeys)}
            />
          ))}
      </Accordion>
    </div>
  );
};

const getBrand = (shop: b2x.ShopApiDto) =>
  shop.tags
    ?.find((tag) => tag.startsWith('BRAND:'))
    ?.replace('BRAND:', '')
    .toLowerCase() as ShopBrand | undefined;

// Provvisorio: è un asset della nostra build (hash nel nome), da sostituire con l'URL su repo.lottomaticagroup.com appena il cliente lo fornisce.
const betterLogoUrl = 'https://www.lottomatica.it/store-locator/static/media/better.c3f9b7fb72ca839bd9b8e32651f710dc.svg';

/*
  Brand JSON-LD di ogni sito, come da template del cliente: @id del nodo Brand iniettato da GTM, logo
  e brand dei negozi (tag BRAND:) che vi appartengono. Lottomatica Servizi non ha un template, quindi nessun brand.
*/
const jsonLdBrandBySite: Record<Brand, (b2x.JsonLdBrand & { shopBrands: Array<ShopBrand> }) | undefined> = {
  goldbet: {
    id: 'https://www.goldbet.it/#brand',
    logoUrl: 'https://repo.lottomaticagroup.com/intplatform/common/svg/logolegale-goldbet.svg',
    // Intralot è stato acquisito da Goldbet: i negozi mantengono il nome Intralot ma appartengono al brand Goldbet.
    shopBrands: ['goldbet', 'intralot'],
  },
  lottomatica: {
    id: 'https://www.lottomatica.it/#brand-better',
    logoUrl: betterLogoUrl,
    // I negozi con tag Lottomatica sono trattati dal sito come Better (stesso pin e logo).
    shopBrands: ['better', 'lottomatica'],
  },
  lottomaticaservizi: undefined,
  planetwin365: {
    id: 'https://www.planetwin365.it/#brand',
    logoUrl: 'https://repo.lottomaticagroup.com/intplatform/common/svg/logo-planetwin-yellow.svg',
    shopBrands: ['planetwin365'],
  },
};

// Brand di cui parla la pagina lista dello store locator (about del WebPage): quello del sito servito, se ha un template.
const getSiteJsonLdBrand = (siteBrand: Brand): b2x.JsonLdBrand | undefined => {
  const jsonLdBrand = jsonLdBrandBySite[siteBrand];
  return jsonLdBrand && { id: jsonLdBrand.id, logoUrl: jsonLdBrand.logoUrl };
};

/*
  Brand a cui collegare il negozio nel JSON-LD della scheda, solo se il brand del negozio appartiene al sito servito:
  altrimenti nessun brand, per non dichiarare relazioni sbagliate.
*/
export const getShopJsonLdBrand = (shop: b2x.ShopApiDto, siteBrand: Brand): b2x.JsonLdBrand | undefined => {
  const shopBrand = getBrand(shop);
  if (shopBrand === undefined || !jsonLdBrandBySite[siteBrand]?.shopBrands.includes(shopBrand)) {
    return undefined;
  }
  return getSiteJsonLdBrand(siteBrand);
};

const shopBrandToPin = (shopBrand?: ShopBrand) => {
  let pin: string = pinBetter;
  switch (shopBrand) {
    case 'better':
    case 'lottomatica':
      pin = pinBetter;
      break;
    case 'goldbet':
      pin = pinGolbet;
      break;
    case 'intralot':
      pin = pinIntralot;
      break;
    case 'planetwin365':
      pin = pinPlanetwin365;
      break;
    // Mantengo planetpay per retrocompatibilità
    case 'lottomaticaservizi':
    case 'planetpay365':
      pin = pinLottomaticaServizi;
      break;
  }
  return pin;
};

const getMarkerContent = (shop: b2x.ShopApiDto): b2x.MarkerContent => {
  const shopBrand = getBrand(shop) as ShopBrand | undefined;

  return {
    image: {
      src: shopBrandToPin(shopBrand),
    },
  };
};

const getDetailsPanelMarkerContent = (shop: b2x.ShopApiDto): b2x.MarkerContent => {
  const shopBrand = getBrand(shop) as ShopBrand | undefined;

  return {
    image: {
      src: shopBrandToPin(shopBrand),
      width: 30,
    },
  };
};

export const getShopName = (shop: b2x.ShopApiDto) => {
  const brand = getBrand(shop);
  // Questo serve perché negli altri store i nomi dei punti vendita sono il nome del brand stesso (es: Better) invece qua sono il vero nome del punto vendita
  // Mantengo planetpay per retrocompatibilità
  if (brand === 'planetpay365' || brand === 'lottomaticaservizi') {
    return shop.name || '';
  }
  return _.capitalize(brand);
};

export const getShopDescription = (shop: b2x.ShopApiDto) => {
  return getTranslatedShopTypes(shop).join(' - ');
};

const getShopServices = (services: Array<string>): Array<{ icon: string; name: string }> => {
  const shopServices: Array<{ icon: string; name: string }> = [];

  services.forEach((service) => {
    if (service.includes('#S01')) {
      shopServices.push({ icon: 'Sports', name: 'SPORTS' });
    } else if (service.includes('#S02')) {
      shopServices.push({ icon: 'Virtual', name: 'VIRTUAL' });
    } else if (service.includes('#S03')) {
      shopServices.push({ icon: 'Ippica', name: 'HORSE_RACING' });
    } else if (service.includes('#S04')) {
      shopServices.push({ icon: 'Totocalcio', name: 'TOTOCALCIO' });
    } else if (service.includes('#S05')) {
      shopServices.push({ icon: 'Ricarica conto gioco', name: 'REFILLS' });
    } else if (service.includes('#S06')) {
      shopServices.push({ icon: 'Apertura conto gioco', name: 'ACCOUNT_OPENING' });
    } else if (service.includes('#S07')) {
      shopServices.push({ icon: 'Prelievo conto gioco', name: 'PAYMENT_USERS' });
    }
    // Servizi di planet pay: sono gestiti in modo custom
    else if (service.includes('#PS01')) {
      shopServices.push({ icon: 'Ricariche telefoniche', name: 'PHONE_RECHARGE' });
    } else if (service.includes('#PS02')) {
      shopServices.push({ icon: 'Pagamento bollettini, MAV, RAV, PAGOPA', name: 'PAYMENT_SERVICES' });
    } else if (service.includes('#PS03')) {
      shopServices.push({ icon: 'Gift Cards, Abbonamenti, Vouchers', name: 'GIFT_VOUCHER' });
    } else if (service.includes('#PS04')) {
      shopServices.push({ icon: 'Visure', name: 'VISURE' });
    } else if (service.includes('#PS05')) {
      shopServices.push({ icon: 'Spedizioni', name: 'SHIPPING' });
    } else if (service.includes('#PS06')) {
      shopServices.push({ icon: 'Biglietteria eventi', name: 'EVENTS_TICKETS' });
    } else if (service.includes('#PS07')) {
      shopServices.push({ icon: 'Biglietteria sport', name: 'SPORTS_TICKETS' });
    } else if (service.includes('#PS08')) {
      shopServices.push({ icon: 'Locker', name: 'LOCKER' });
    } else if (service.includes('#PS09')) {
      shopServices.push({ icon: 'Deposito bagagli', name: 'LUGGAGE_STORAGE' });
    }
  });

  return shopServices;
};

const getShopBettingServices = (shop: b2x.ShopApiDto): Array<{ icon: string; name: string }> | undefined => {
  const services = shop.tags?.filter((tag) => tag.includes('SERVIZIO:G01'));

  return services && getShopServices(services);
};

const getShopOnlineGamesServices = (shop: b2x.ShopApiDto): Array<{ icon: string; name: string }> | undefined => {
  const services = shop.tags?.filter((tag) => tag.includes('SERVIZIO:G02'));

  return services && getShopServices(services);
};

const getShopOtherServices = (shop: b2x.ShopApiDto): Array<{ icon: string; name: string }> | undefined => {
  const services = shop.tags?.filter((tag) => tag.includes('SERVIZIO:G03'));
  return services && getShopServices(services);
};

// Mantengo planetpay per retrocompatibilità
// PPAY era dedicato ai codici per planet pay, ora rappresentano anche quelli per lottomaticaservizi
const getShopPPServices = (shop: b2x.ShopApiDto): Array<{ icon: string; name: string }> | undefined => {
  const services = shop.tags?.filter((tag) => tag.includes('SERVIZIO:PPAY'));
  return services && getShopServices(services);
};

const getShopThumbnailSrc = (shop: b2x.ShopApiDto) => {
  const shopBrand = getBrand(shop);

  switch (shopBrand) {
    case 'better':
    case 'lottomatica':
      return logoBetter;
    case 'goldbet':
      return logoGolbet;
    case 'intralot':
      return logoIntralot;
    case 'planetwin365':
      return logoPlanetwin365;
    // Mantengo planetpay per retrocompatibilità
    case 'planetpay365':
    case 'lottomaticaservizi':
      return logoLottomaticaServizi;
    default:
      return logoBetter;
  }
};

export const getShopTypes = (shop: b2x.ShopApiDto): Array<string> => {
  const shopTypes = [];
  shop.tags?.find((tag) => tag.includes('SERVIZIO:G01')) && shopTypes.push('BETTING'); // Scommesse
  shop.tags?.find((tag) => tag.includes('SERVIZIO:G02')) && shopTypes.push('ONLINE_GAMES'); // Gioco online
  shop.tags?.find((tag) => tag.includes('SERVIZIO:G03')) && shopTypes.push('OTHER_SERVICES'); // Altri servizi
  shop.tags?.find((tag) => tag.includes('SERVIZIO:PPAY')) && shopTypes.push('PLANET_PAY'); // Planet Pay
  return shopTypes;
};

export const getTranslatedShopTypes = (shop: b2x.ShopApiDto): Array<string> => {
  const shopTypes = getShopTypes(shop);
  return shopTypes.map((shopType) => t(`shopTypes.${shopType}` as ResourceKeys));
};
